import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaApple } from 'react-icons/fa';
import { LuEye, LuEyeOff, LuLock, LuLogIn, LuMail } from 'react-icons/lu';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  AuthAlert,
  AuthDivider,
  AuthField,
  AuthLegalLink,
  AuthShell,
  GoogleMark,
} from '../components/auth/AuthShell';
import PendingJoinNote from '../components/circles/PendingJoinNote';
import { readPendingJoin } from '../components/circles/pendingCircleJoin';
import { Button } from '../design-system/components';
import { getStashedReferralCode } from '../services/referral.service';
import { BANNED_FALLBACK, useAccountBanStore } from '../stores/account-ban.store';
import { useAuthStore } from '../stores/auth.store';
import { fireAndForget } from '../utils/fireAndForget';
import { buildOAuthState } from '../utils/oauthState';
import { googleOAuthStartUrl } from '../services/google-app-nonce';
import { signedInTarget, withRedirect } from '../utils/safe-redirect';
import { getStashedSignupSource, getStashedSignupVariant } from '../utils/signup-source';

interface LoginFormData {
  email: string;
  password: string;
}

/**
 * Login Component
 * Handles user authentication through a login form
 */
function Login() {
  // Navigation hook and URL params
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Form state management
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);

  // Sign in with Apple is shown only on iOS (Apple Review Guideline 4.8). Web +
  // Android already offer Google login and are not subject to the requirement;
  // the native flow needs no web Services-ID setup this way. See
  // docs/plans/SIGN-IN-WITH-APPLE.md.
  const isApplePlatform = Capacitor.getPlatform() === 'ios';
  // React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = useForm<LoginFormData>({ mode: 'all' });
  const [loginError, setLoginError] = useState('');
  // Read once: a pending circle join the public notice (or a register link) left.
  const [pendingJoin] = useState(() => readPendingJoin());

  // Auth store actions and state
  const login = useAuthStore((state) => state.login);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isGoogleLoading = useAuthStore((state) => state.isGoogleLoading);
  const setGoogleLoading = useAuthStore((state) => state.setGoogleLoading);
  const signInWithApple = useAuthStore((state) => state.signInWithApple);

  const emailValue = watch('email');
  const passwordValue = watch('password');

  // Clear login error when email or password is changed.
  // Why: we only want to fire on user input changes; including loginError would
  // re-fire immediately after clearing it (no-op but unnecessary).
  useEffect(() => {
    if (loginError) setLoginError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailValue, passwordValue]);

  // Handle OAuth errors from URL parameters
  useEffect(() => {
    const error = searchParams.get('error');
    if (error === 'account_banned') {
      // A banned Google sign-in: the notice came as an httpOnly cookie, never
      // in this URL; redeem it for the suspended screen.
      setGoogleLoading(false);
      void useAccountBanStore
        .getState()
        .redeemNotice()
        .then((outcome) => {
          if (outcome === 'unavailable') toast.error(BANNED_FALLBACK);
        });
      void navigate('/login', { replace: true });
      return;
    }
    if (error) {
      // Reset Google loading state on error
      setGoogleLoading(false);

      const errorMessages: Record<string, string> = {
        oauth_failed: 'Google login failed. Please try again.',
        oauth_data_error: 'Google login error: Invalid user data received.',
        oauth_missing_data: 'Google login error: Missing authentication data.',
        // The callback did not come from a sign-in this browser started
        // (expired, replayed, or a link someone else sent): AuthController
        // GOOGLE_STATE_ERROR.
        oauth_state_invalid: 'Google sign-in expired. Please try again.',
      };

      const message = errorMessages[error] || 'Authentication error occurred.';
      toast.error(message);

      // Clear the error from URL
      void navigate('/login', { replace: true });
    }
  }, [searchParams, navigate, setGoogleLoading]);

  // Redirect if already authenticated
  // True from this page's own sign-in until its post-auth steps are done.
  const postAuthPending = useRef(false);
  useEffect(() => {
    if (isAuthenticated) {
      // A sign-in this page started finishes its own post-auth steps first
      // and navigates when they are done (finishSignIn); PublicRoute leaves a
      // session that arrives while the page is open to the page (items 4/9).
      if (postAuthPending.current) return;
      void navigate(signedInTarget(searchParams.get('redirect')), { replace: true });
    }
  }, [isAuthenticated, navigate, searchParams]);

  /** After this page's own sign-in and its post-auth steps: follow the redirect. */
  const finishSignIn = () => {
    postAuthPending.current = false;
    if (useAuthStore.getState().isAuthenticated) {
      void navigate(signedInTarget(searchParams.get('redirect')), { replace: true });
    }
  };

  /**
   * Handle form submission
   * Attempts to log in the user with provided credentials
   */
  async function submitLoginForm(postData: LoginFormData) {
    setIsLoginLoading(true);
    postAuthPending.current = true;
    setLoginError(''); // Clear any previous errors

    try {
      const { error } = await login(postData.email, postData.password);
      // On success the page change says it worked: no toast (David, 2026-10-29).
      if (error) setLoginError(error);
    } catch {
      setLoginError('An unexpected error occurred');
    } finally {
      setIsLoginLoading(false);
      finishSignIn();
    }
  }

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);

    try {
      // Detect if we're in a mobile app environment
      const isMobileApp = Capacitor.isNativePlatform();

      // Add state parameter to indicate mobile app
      // Carry the stashed invite code through OAuth: localStorage can't reach the
      // backend callback, but the state param is echoed back by Google verbatim, so
      // it's the one channel that survives the round-trip. Parsed + format-validated
      // server-side; only used if this sign-in creates a NEW account.
      const referralCode = getStashedReferralCode();
      const clientType = isMobileApp ? 'mobile_app' : 'web_browser';
      // isGuest rides along as `src:guest_tour` so a guest-tour conversion via
      // Google is attributed server-side (new accounts only).
      const state = buildOAuthState(
        clientType,
        referralCode,
        useAuthStore.getState().isGuest,
        getStashedSignupSource(),
        getStashedSignupVariant(),
      );
      // The app's start carries a fresh device nonce the deep link must echo
      // back, so a link this device did not start cannot sign it in.
      const googleOAuthUrl = googleOAuthStartUrl(import.meta.env.VITE_API_URL, state, isMobileApp);

      if (isMobileApp) {
        // Open in system browser (Chrome Custom Tabs / ASWebAuthenticationSession)
        // WebViews are blocked by Google OAuth (403: disallowed_useragent)
        toast.info(
          'Opening Google login... Please complete authentication and return to the app.',
          {
            autoClose: 5000,
          },
        );
        await Browser.open({ url: googleOAuthUrl });
        // Reset loading after browser opens — user will return to app later
        setGoogleLoading(false);
      } else {
        // Web: navigates away, so loading state doesn't matter
        window.location.href = googleOAuthUrl;
      }
    } catch {
      toast.error('Failed to open Google login');
      setGoogleLoading(false);
    }
  };

  /**
   * Sign in with Apple (iOS only). Uses the native authorization sheet to
   * obtain an identity token, then exchanges it for a Sanctum token via the
   * auth store (same end state as Google/email login).
   */
  const handleAppleLogin = async () => {
    setIsAppleLoading(true);
    postAuthPending.current = true;

    try {
      const result = await SignInWithApple.authorize({
        // clientId/redirectURI are required by the plugin's type but are only
        // used by its web flow; the native iOS flow uses the app's entitlement.
        clientId: 'app.empathease.empathease',
        redirectURI: `${import.meta.env.VITE_APP_URL ?? 'https://empathease.app'}/auth/apple/callback`,
        scopes: 'name email',
      });

      const identityToken = result.response?.identityToken;
      if (!identityToken) {
        toast.error('Apple did not return an identity token. Please try again.');
        return;
      }

      const { error } = await signInWithApple({
        identityToken,
        authorizationCode: result.response.authorizationCode,
        givenName: result.response.givenName,
        familyName: result.response.familyName,
        email: result.response.email,
      });

      // On success the page change says it worked: no toast.
      if (error) toast.error(error);
    } catch (err) {
      // authorize() rejects on user cancellation as well as real failures.
      console.error('Apple sign-in failed:', err);
      toast.error('Apple sign-in was cancelled or could not be completed.');
    } finally {
      setIsAppleLoading(false);
      finishSignIn();
    }
  };

  return (
    <AuthShell
      title="Welcome"
      accent="Back"
      tagline="Login to continue your NVC journey"
      footer={
        <p className="ee-auth-terms">
          By signing in, you agree to our{' '}
          <AuthLegalLink to="/terms-of-use">Terms of Service</AuthLegalLink> and{' '}
          <AuthLegalLink to="/privacy">Privacy Policy</AuthLegalLink>
        </p>
      }
    >
      {/* A visitor who chose a circle on the public notice and has an account (§5). */}
      <PendingJoinNote join={pendingJoin} mode="login" />

      {/* Form-level authentication error */}
      {loginError ? <AuthAlert>{loginError}</AuthAlert> : null}

      <form className="ee-auth-form" onSubmit={fireAndForget(handleSubmit(submitLoginForm))}>
        <AuthField id="email" label="Email" icon={LuMail} error={errors?.email?.message}>
          <input
            type="email"
            id="email"
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i,
                message: 'Enter a valid email',
              },
            })}
            placeholder="Enter your email"
          />
        </AuthField>

        <AuthField
          id="password"
          label="Password"
          icon={LuLock}
          error={errors?.password?.message}
          trailing={
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              className="ee-auth-field__toggle"
            >
              {showPassword ? <LuEyeOff aria-hidden="true" /> : <LuEye aria-hidden="true" />}
            </button>
          }
        >
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Password must be at least 8 characters' },
            })}
            placeholder="Enter your password"
          />
        </AuthField>

        {/* Forgot Password Link */}
        <Link
          to={withRedirect('/forgot-password', searchParams.get('redirect'))}
          className="ee-auth-link ee-auth-forgot"
        >
          Forgot password?
        </Link>

        {/* Login Button */}
        <Button
          type="submit"
          fullWidth
          disabled={!isValid}
          loading={isLoginLoading}
          leadingIcon={<LuLogIn />}
        >
          {isLoginLoading ? 'Signing you in...' : 'Sign In'}
        </Button>
      </form>

      <AuthDivider />

      {/* Google Login */}
      <Button
        variant="secondary"
        fullWidth
        onClick={fireAndForget(handleGoogleLogin)}
        disabled={isLoginLoading}
        loading={isGoogleLoading}
        leadingIcon={<GoogleMark />}
      >
        {isGoogleLoading ? 'Redirecting to Google...' : 'Continue with Google'}
      </Button>

      {/* Sign in with Apple: iOS only (Apple Review Guideline 4.8) */}
      {isApplePlatform ? (
        <Button
          variant="secondary"
          fullWidth
          className="ee-auth-apple"
          onClick={fireAndForget(handleAppleLogin)}
          disabled={isLoginLoading || isGoogleLoading}
          loading={isAppleLoading}
          leadingIcon={<FaApple />}
        >
          {isAppleLoading ? 'Signing in with Apple...' : 'Sign in with Apple'}
        </Button>
      ) : null}

      {/* Registration Link */}
      <p className="ee-auth-switch">
        Don&apos;t have an account?{' '}
        <Link className="ee-auth-link" to={withRedirect('/register', searchParams.get('redirect'))}>
          Sign Up
        </Link>
      </p>
    </AuthShell>
  );
}

export default Login;
